import { computed, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 280;
const MAX_WIDTH = 560;

const clampWidth = (width, minWidth, maxWidth) =>
  Math.max(minWidth, Math.min(maxWidth, width));

export function useRightPanelResize({
  storageKey = 'right_panel_width',
  defaultWidth = DEFAULT_WIDTH,
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
} = {}) {
  const { uiSettings, updateUISettings } = useUISettings();
  const isRTL = useMapGetter('accounts/isRTL');

  const panelWidth = ref(
    clampWidth(
      uiSettings.value?.[storageKey] || defaultWidth,
      minWidth,
      maxWidth
    )
  );
  const isResizing = ref(false);
  const startX = ref(0);
  const startWidth = ref(0);

  const panelStyle = computed(() => ({
    width: `${panelWidth.value}px`,
    minWidth: `${panelWidth.value}px`,
    maxWidth: `${panelWidth.value}px`,
    flexBasis: `${panelWidth.value}px`,
  }));

  const setPanelWidth = width => {
    panelWidth.value = clampWidth(width, minWidth, maxWidth);
  };

  const savePanelWidth = () => {
    updateUISettings({ [storageKey]: panelWidth.value });
  };

  const resetPanelWidth = () => {
    panelWidth.value = defaultWidth;
    updateUISettings({ [storageKey]: defaultWidth });
  };

  const getClientX = event =>
    event.touches ? event.touches[0].clientX : event.clientX;

  const onResizeStart = event => {
    isResizing.value = true;
    startX.value = getClientX(event);
    startWidth.value = panelWidth.value;
    Object.assign(document.body.style, {
      cursor: 'col-resize',
      userSelect: 'none',
    });
    event.preventDefault();
  };

  const onResizeMove = event => {
    if (!isResizing.value) return;

    const delta = isRTL.value
      ? getClientX(event) - startX.value
      : startX.value - getClientX(event);
    setPanelWidth(startWidth.value + delta);
  };

  const onResizeEnd = () => {
    if (!isResizing.value) return;

    isResizing.value = false;
    Object.assign(document.body.style, { cursor: '', userSelect: '' });
    savePanelWidth();
  };

  watch(
    () => uiSettings.value?.[storageKey],
    width => {
      if (isResizing.value) return;
      setPanelWidth(width || defaultWidth);
    }
  );

  useEventListener(document, 'mousemove', onResizeMove);
  useEventListener(document, 'mouseup', onResizeEnd);
  useEventListener(document, 'touchmove', onResizeMove, { passive: false });
  useEventListener(document, 'touchend', onResizeEnd);
  useEventListener(document, 'pointermove', onResizeMove);
  useEventListener(document, 'pointerup', onResizeEnd);

  return {
    panelWidth,
    panelStyle,
    isResizing,
    onResizeStart,
    resetPanelWidth,
    minWidth,
    maxWidth,
    defaultWidth,
  };
}
